import { push, replace } from 'connected-react-router';
import qs from 'qs';
import {
    selectMyTraderaState,
    searching,
    error
} from 'tradera-apps/my-tradera/app/state/reducers/my-tradera';
import purchaseOrderActions from './purchase-order';
import { endItems } from '../../helpers/item-actions-helper';
import { Analytics, logger } from 'packages';
import { getAvailableQueryParams } from '../../helpers/route-helper';
import URLS from '../../constants/urls';
import fetch from '../../utils/fetch';
import featureToggles from '../../utils/feature-toggles';
import triot from 'triot';
import { selectCookiesState } from 'tradera-apps/my-tradera/app/state/reducers/cookies';
import { selectFiltersState } from 'tradera-apps/my-tradera/app/state/selectors/filters';
import { selectMyTraderaItemsState } from 'tradera-apps/my-tradera/app/state/reducers/my-tradera-items';
import { selectRouterState } from 'tradera-apps/my-tradera/app/state/selectors/router';
import { scrollToInitialScroll } from 'tradera-apps/my-tradera/app/state/actions/scroll-to-initial-scroll';
import { webApiClient } from 'tradera-utils/http';

/**
 * @param {function} dispatch dispatch redux action
 * @param {err} error error relayed from asynch call
 */
const handleError = (dispatch, e) => {
    dispatch(searching(false));
    dispatch(error(true));
    logger(e);
};

const actionTypes = {
    BUYER_ACTIVE_ITEMS_HIDE: 'BUYER_ACTIVE_ITEMS_HIDE',
    BUYER_ACTIVE_ITEMS_SHOW: 'BUYER_ACTIVE_ITEMS_SHOW',
    UPDATE_ITEMS: 'UPDATE_ITEMS',
    COMPLETED_SEARCH: 'COMPLETED_SEARCH',
    OPEN_MORE: 'OPENED_MORE_BUTTON_DROPDOWN',
    SELECT_ITEM: 'SELECT_ITEM_CHECKBOX',
    SELECT_ALL_ITEMS: 'SELECT_ALL_ITEMS',
    REMOVE_ITEMS: 'REMOVE_ITEMS',
    OPEN_DEL_LISTING_ITEM: 'OPEN_DELETE_LISTING_ITEM',
    REMOVE_ALL_SELECTED: 'REMOVE_ALL_SELECTED_CHECKBOXES',
    UPDATE_ITEMS_WITH_ERROR: 'UPDATED_ALL_ITEMS_DUE_TO_ERROR',
    PURCHASE_ORDERS_MARKED_AS_PAID_BY_SELLER:
        'PURCHASE_ORDERS_MARKED_AS_PAID_BY_SELLER',
    PURCHASE_ORDERS_MARKED_AS_PAID_BY_BUYER:
        'PURCHASE_ORDERS_MARKED_AS_PAID_BY_BUYER',
    PURCHASE_ORDERS_UNMARKED_AS_PAID_BY_SELLER:
        'PURCHASE_ORDERS_UNMARKED_AS_PAID_BY_SELLER',
    PURCHASE_ORDERS_UNMARKED_AS_PAID_BY_BUYER:
        'PURCHASE_ORDERS_UNMARKED_AS_PAID_BY_BUYER',
    PURCHASE_ORDERS_HIDDEN_BY_SELLER: 'PURCHASE_ORDERS_HIDDEN_BY_SELLER',
    PURCHASE_ORDERS_HIDDEN_BY_BUYER: 'PURCHASE_ORDERS_HIDDEN_BY_BUYER',
    PURCHASE_ORDERS_SHOWN_BY_SELLER: 'PURCHASE_ORDERS_SHOWN_BY_SELLER',
    PURCHASE_ORDERS_SHOWN_BY_BUYER: 'PURCHASE_ORDERS_SHOWN_BY_BUYER',
    ITEM_HIDDEN: 'ITEM_HIDDEN',
    ITEM_SHOWN: 'ITEM_SHOWN',
    PURCHASE_ORDERS_MARKED_AS_SHIPPED_BY_SELLER:
        'PURCHASE_ORDERS_MARKED_AS_SHIPPED_BY_SELLER',
    PURCHASE_ORDERS_UNMARKED_AS_SHIPPED_BY_SELLER:
        'PURCHASE_ORDERS_UNMARKED_AS_SHIPPED_BY_SELLER',
    PURCHASE_ORDERS_UNMARKED_AS_SHIPPING_BOOKED:
        'PURCHASE_ORDERS_UNMARKED_AS_SHIPPING_BOOKED',
    PURCHASE_ORDERS_MARKED_AS_SHIPPING_BOOKED:
        'PURCHASE_ORDERS_MARKED_AS_SHIPPING_BOOKED',
    SHIPMENT_DELETED: 'SHIPMENT_DELETED',
    FEEDBACK_LEFT_BY_SELLER: 'FEEDBACK_LEFT_BY_SELLER',
    FEEDBACK_LEFT_BY_BUYER: 'FEEDBACK_LEFT_BY_BUYER',
    RESET_ITEMS: 'RESET_ITEMS',
    SET_RESTARTED: 'SET_RESTARTED',
    SET_NEW_SHIPPING_PRICE: 'SET_NEW_SHIPPING_PRICE'
};

const updateItems = (
    value,
    page,
    listViewType,
    memberMessagingParticipation
) => ({
    type: actionTypes.UPDATE_ITEMS,
    payload: {
        value,
        page,
        listViewType,
        memberMessagingParticipation
    }
});

const updateItemsWithError = () => ({
    type: actionTypes.UPDATE_ITEMS_WITH_ERROR
});

const openMore = (itemId, pageName = false) => {
    if (pageName) {
        Analytics.trackEvent({
            category: pageName,
            action: 'Toggle',
            label: 'More'
        });
    }

    return {
        type: actionTypes.OPEN_MORE,
        payload: itemId
    };
};

const selectItem = (itemId, listViewType, selectedFilterBar) => ({
    type: actionTypes.SELECT_ITEM,
    payload: {
        id: itemId,
        listViewType,
        selectedFilterBar
    }
});

const resetSelectedCheckboxes = () => ({
    type: actionTypes.REMOVE_ALL_SELECTED
});

const selectAllItems = (select, listViewType, selectedFilterBar, memberId) => ({
    type: actionTypes.SELECT_ALL_ITEMS,
    payload: {
        select,
        listViewType,
        selectedFilterBar,
        memberId
    }
});

const removeItems = itemIds => ({
    type: actionTypes.REMOVE_ITEMS,
    payload: itemIds
});

const updateURL = url => (dispatch, getState) => {
    const state = getState();
    const locationDescriptor = {
        pathname: url,
        search: qs.stringify(getAvailableQueryParams(state))
    };

    if (!window.location.search) {
        dispatch(replace(locationDescriptor));
    } else {
        dispatch(push(locationDescriptor));
    }
};

const purchaseOrdersMarkAsPaidBySeller = (
    purchaseOrderIds,
    listViewType,
    messagingParticipation
) => ({
    type: actionTypes.PURCHASE_ORDERS_MARKED_AS_PAID_BY_SELLER,
    payload: {
        ids: purchaseOrderIds,
        listViewType,
        messagingParticipation
    }
});

const purchaseOrdersMarkAsPaidByBuyer = (purchaseOrderIds, listViewType) => ({
    type: actionTypes.PURCHASE_ORDERS_MARKED_AS_PAID_BY_BUYER,
    payload: {
        ids: purchaseOrderIds,
        listViewType: listViewType
    }
});

const purchaseOrdersUnmarkAsPaidBySeller = (
    purchaseOrderIds,
    listViewType,
    messagingParticipation
) => ({
    type: actionTypes.PURCHASE_ORDERS_UNMARKED_AS_PAID_BY_SELLER,
    payload: {
        ids: purchaseOrderIds,
        listViewType,
        messagingParticipation
    }
});

const purchaseOrdersUnmarkAsPaidByBuyer = (purchaseOrderIds, listViewType) => ({
    type: actionTypes.PURCHASE_ORDERS_UNMARKED_AS_PAID_BY_BUYER,
    payload: {
        ids: purchaseOrderIds,
        listViewType: listViewType
    }
});

const transactionsMarkAsShippedBySeller = (transactionIds, listViewType) => ({
    type: actionTypes.PURCHASE_ORDERS_MARKED_AS_SHIPPED_BY_SELLER,
    payload: {
        ids: transactionIds,
        listViewType: listViewType
    }
});

const transactionsUnmarkAsShippedBySeller = (transactionIds, listViewType) => ({
    type: actionTypes.PURCHASE_ORDERS_UNMARKED_AS_SHIPPED_BY_SELLER,
    payload: {
        ids: transactionIds,
        listViewType: listViewType
    }
});

const transactionsMarkAsShippingBooked = (purchaseOrderIds, listViewType) => ({
    type: actionTypes.PURCHASE_ORDERS_MARKED_AS_SHIPPING_BOOKED,
    payload: {
        ids: purchaseOrderIds,
        listViewType: listViewType
    }
});

const transactionsUnmarkAsShippingBooked = (
    purchaseOrderIds,
    listViewType
) => ({
    type: actionTypes.PURCHASE_ORDERS_UNMARKED_AS_SHIPPING_BOOKED,
    payload: {
        ids: purchaseOrderIds,
        listViewType: listViewType
    }
});

const transactionDeleteShipment = (purchaseOrderId, listViewType) => ({
    type: actionTypes.SHIPMENT_DELETED,
    payload: {
        purchaseOrderId,
        listViewType
    }
});

const buyerActiveItemsHide = (itemIds, listViewType) => ({
    type: actionTypes.BUYER_ACTIVE_ITEMS_HIDE,
    payload: {
        ids: itemIds,
        listViewType: listViewType
    }
});

const buyerActiveItemsShow = (itemIds, listViewType) => ({
    type: actionTypes.BUYER_ACTIVE_ITEMS_SHOW,
    payload: {
        ids: itemIds,
        listViewType: listViewType
    }
});

const purchaseOrdersHiddenBySeller = (purchaseOrderIds, listViewType) => ({
    type: actionTypes.PURCHASE_ORDERS_HIDDEN_BY_SELLER,
    payload: {
        ids: purchaseOrderIds,
        listViewType: listViewType
    }
});

const transactionsHiddenByBuyer = (transactionIds, listViewType) => ({
    type: actionTypes.PURCHASE_ORDERS_HIDDEN_BY_BUYER,
    payload: {
        ids: transactionIds,
        listViewType: listViewType
    }
});

const purchaseOrdersShownBySeller = (purchaseOrderIds, listViewType) => ({
    type: actionTypes.PURCHASE_ORDERS_SHOWN_BY_SELLER,
    payload: {
        ids: purchaseOrderIds,
        listViewType: listViewType
    }
});

const transactionsShownByBuyer = (transactionIds, listViewType) => ({
    type: actionTypes.PURCHASE_ORDERS_SHOWN_BY_BUYER,
    payload: {
        ids: transactionIds,
        listViewType: listViewType
    }
});

const unsoldItemsHidden = (itemIds, listViewType) => ({
    type: actionTypes.ITEM_HIDDEN,
    payload: {
        ids: itemIds,
        listViewType: listViewType
    }
});

const unsoldItemsShown = (itemIds, listViewType) => ({
    type: actionTypes.ITEM_SHOWN,
    payload: {
        ids: itemIds,
        listViewType: listViewType
    }
});

const feedbackLeftBySeller = (transactionId, listViewType) => ({
    type: actionTypes.FEEDBACK_LEFT_BY_SELLER,
    payload: {
        id: transactionId,
        listViewType: listViewType
    }
});

const feedbackLeftByBuyer = (transactionId, listViewType) => ({
    type: actionTypes.FEEDBACK_LEFT_BY_BUYER,
    payload: {
        id: transactionId,
        listViewType: listViewType
    }
});

const resetMyTraderaItems = () => ({
    type: actionTypes.RESET_ITEMS
});

const cancelItem = item => dispatch => {
    dispatch(searching(true));
    endItems([item.itemId])
        .then(response => {
            if (response && response.itemId) {
                dispatch(removeItems(response.itemId));
            }
            dispatch(resetSelectedCheckboxes());
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const setRestarted = (itemIds, listViewType) => ({
    type: actionTypes.SET_RESTARTED,
    payload: {
        itemIds: itemIds,
        listViewType: listViewType
    }
});

/**
 * Tmp function until backend has changed its api
 * Manipulate paging params before sending to API;
 * @param {object} params - current state.filters.modifiers;
 */
const changeParamsForBackend = params => {
    const skip =
        params.paging && params.paging.page > 0
            ? (params.paging.page - 1) * params.paging.size
            : 0;
    const take =
        params.paging && params.paging.page > 0 ? params.paging.size * 1 : 25;

    const newPaging = {
        skip: skip < 0 ? 0 : skip,
        take: take
    };

    return {
        ...params,
        paging: newPaging
    };
};

const runSearch = (
    page,
    shouldScrollToInitialScroll = false,
    listViewType,
    waitForBackend = false,
    dontModifyHistory = true
) => async (dispatch, getState) => {
    if (!featureToggles.isMyTraderaElasticEnabled) {
        return;
    }

    const endPoint = page && page.searchUrl;

    if (!endPoint) {
        return;
    }

    const search = async () => {
        const state = getState();

        dispatch(searching(true));
        dispatch(error(false));
        dispatch(purchaseOrderActions.resetPurchaseOrder());
        dispatch(resetMyTraderaItems());
        dispatch(resetSelectedCheckboxes());

        try {
            const modifiers = selectFiltersState(state).modifiers;

            const data = await fetch(endPoint, {
                body: JSON.stringify(changeParamsForBackend(modifiers))
            });
            dispatch(
                updateItems(
                    data,
                    page.pageName,
                    listViewType,
                    selectMyTraderaState(state).memberMessagingParticipation
                )
            );

            if (shouldScrollToInitialScroll === true) {
                scrollToInitialScroll();
            }
            dispatch(searching(false));
            return dispatch(error(false));
        } catch (error) {
            dispatch(updateItemsWithError());
            if (dontModifyHistory === false) {
                updateURL(
                    dispatch,
                    page.url,
                    selectFiltersState(state).modifiers
                );
            }
            handleError(dispatch, error);
        }
    };

    // This is a hack to enable delay of searches since sometimes when modifying an item
    // outside of the SPA world, it could take some time for the backend to catch up
    if (waitForBackend === true) {
        setTimeout(search, 3000);
    } else {
        search();
    }
};

const rebuildList = () => (dispatch, getState) => {
    const existingState = { ...selectMyTraderaItemsState(getState()) };
    if (!existingState.itemsByID) {
        return dispatch(error(true));
    }
    const state = getState();

    const pageName =
        selectMyTraderaState(state).page &&
        selectMyTraderaState(state).page[
            selectRouterState(state).location.pathname
        ] &&
        selectMyTraderaState(state).page[
            selectRouterState(state).location.pathname
        ].pageName;
    const items = existingState.itemsOrderedByID.map(
        i => existingState.itemsByID[i]
    );
    const newState = {
        currentTime: existingState.currentTime,
        paging: existingState.paging,
        statistics: existingState.statistics,
        items: items
    };
    dispatch(
        updateItems(
            newState,
            pageName,
            selectCookiesState(getState()).LIST_VIEW_TYPE
        )
    );
};

const contactHighestBidder = (item, sellerEmail) => () => {
    const payload = {
        modalTitle: 'Kontakta högsta budgivare',
        messageSubject: `Meddelande från säljare: ${item.listing.shortDescription}`,
        itemEnddate: item.listing.endDate.formatted,
        itemId: item.itemId,
        itemImageUrl: item.listing.images.main.medium,
        itemTitle: item.listing.shortDescription,
        receiverMemberAlias: item.bidInformation.leadingBidder.alias,
        receiverMemberId: item.bidInformation.leadingBidder.id,
        senderMemberEmail: sellerEmail,
        businessWarning: false,
        reloadAfterClose: true
    };

    triot.bus.trigger('ui:contact-modal:show-modal', payload);
};

// From the more-menu
const buyerActiveItemsHideItems = itemIds => dispatch => {
    dispatch(searching(true));
    fetch(URLS.BUYER_ACTIVE_ITEMS_HIDE, {
        body: JSON.stringify({ ids: itemIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(removeItems(itemIds));
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

// From the more-menu
const buyerActiveItemsShowItems = itemIds => dispatch => {
    dispatch(searching(true));
    fetch(URLS.BUYER_ACTIVE_ITEMS_SHOW, {
        body: JSON.stringify({ ids: itemIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(removeItems(itemIds));
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const markPurchaseOrdersAsPaidBySeller = (
    purchaseOrderIds,
    listViewType,
    messagingParticipation
) => dispatch => {
    dispatch(searching(true));
    fetch(URLS.SELLER_MARK_PAID, {
        body: JSON.stringify({ ids: purchaseOrderIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(
                purchaseOrdersMarkAsPaidBySeller(
                    purchaseOrderIds,
                    listViewType,
                    messagingParticipation
                )
            );
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const markPurchaseOrdersAsPaidByBuyer = (
    purchaseOrderIds,
    listViewType
) => dispatch => {
    dispatch(searching(true));
    fetch(URLS.BUYER_MARK_PAID, {
        body: JSON.stringify({ ids: purchaseOrderIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(
                purchaseOrdersMarkAsPaidByBuyer(purchaseOrderIds, listViewType)
            );
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const unmarkPurchaseOrdersAsPaidBySeller = (
    purchaseOrderIds,
    listViewType,
    messagingParticipation
) => dispatch => {
    dispatch(searching(true));
    fetch(URLS.SELLER_UNMARK_PAID, {
        body: JSON.stringify({ ids: purchaseOrderIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(
                purchaseOrdersUnmarkAsPaidBySeller(
                    purchaseOrderIds,
                    listViewType,
                    messagingParticipation
                )
            );
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const unmarkPurchaseOrdersAsPaidByBuyer = (
    purchaseOrderIds,
    listViewType
) => dispatch => {
    dispatch(searching(true));
    fetch(URLS.BUYER_UNMARK_PAID, {
        body: JSON.stringify({ ids: purchaseOrderIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(
                purchaseOrdersUnmarkAsPaidByBuyer(
                    purchaseOrderIds,
                    listViewType
                )
            );
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const sellingUnmarkAsShippingBooked = (
    purchaseOrderIds,
    listViewType
) => dispatch => {
    dispatch(searching(true));
    fetch(URLS.UNMARK_BOOKED, {
        body: JSON.stringify({ ids: purchaseOrderIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(
                transactionsUnmarkAsShippingBooked(
                    purchaseOrderIds,
                    listViewType
                )
            );
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const sellingMarkAsShippingBooked = (
    purchaseOrderId,
    listViewType
) => dispatch => {
    dispatch(searching(true));
    webApiClient
        .post('/member/selling/item-shipping-booked', {
            purchaseOrderId,
            booked: true
        })
        .then(() => {
            dispatch(error(false));
            dispatch(
                transactionsMarkAsShippingBooked(
                    [purchaseOrderId],
                    listViewType
                )
            );
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const sellingDeleteShipment = (
    shipmentId,
    purchaseOrderId,
    listViewType
) => dispatch => {
    dispatch(searching(true));
    fetch(URLS.DELETE_SHIPMENT, {
        body: JSON.stringify({ shipmentId: shipmentId })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(transactionDeleteShipment(purchaseOrderId, listViewType));
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const sellingMarkAsShipped = (transactionIds, listViewType) => dispatch => {
    dispatch(searching(true));
    fetch(URLS.MARK_SHIPPED, {
        body: JSON.stringify({ ids: transactionIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(
                transactionsMarkAsShippedBySeller(transactionIds, listViewType)
            );
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const sellingUnmarkAsShipped = (transactionIds, listViewType) => dispatch => {
    dispatch(searching(true));

    fetch(URLS.UNMARK_SHIPPED, {
        body: JSON.stringify({ ids: transactionIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(
                transactionsUnmarkAsShippedBySeller(
                    transactionIds,
                    listViewType
                )
            );
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const sellingLeaveFeedback = (item, listViewType) => () => {
    const payload = {
        action: 'LeaveFeedbackAsSeller',
        ids: [item.transaction.transactionId],
        itemId: item.itemId,
        listViewType: listViewType
    };
    triot.bus.trigger('ui:list-item-action:on-leave-single-feedback', payload);
    return payload;
};

const sellerUnhideTransaction = (
    purchaseOrderIds,
    listViewType
) => dispatch => {
    dispatch(searching(true));
    fetch(URLS.SELLER_SHOW, {
        body: JSON.stringify({ ids: purchaseOrderIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(
                purchaseOrdersShownBySeller(purchaseOrderIds, listViewType)
            );
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const buyerUnhideTransaction = (transactionIds, listViewType) => dispatch => {
    dispatch(searching(true));
    fetch(URLS.BUYER_SHOW, {
        body: JSON.stringify({ ids: transactionIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(transactionsShownByBuyer(transactionIds, listViewType));
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const sellerHideTransaction = (purchaseOrderIds, listViewType) => dispatch => {
    dispatch(searching(true));
    fetch(URLS.SELLER_HIDE, {
        body: JSON.stringify({ ids: purchaseOrderIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(
                purchaseOrdersHiddenBySeller(purchaseOrderIds, listViewType)
            );
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const buyerHideTransaction = (transactionIds, listViewType) => dispatch => {
    dispatch(searching(true));
    fetch(URLS.BUYER_HIDE, {
        body: JSON.stringify({ ids: transactionIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(transactionsHiddenByBuyer(transactionIds, listViewType));
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const sellerUnhideUnsold = (itemIds, listViewType) => dispatch => {
    dispatch(searching(true));
    fetch(URLS.UNSOLDSHOW, {
        body: JSON.stringify({ ids: itemIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(unsoldItemsShown(itemIds, listViewType));
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const sellerHideUnsold = (itemIds, listViewType) => dispatch => {
    dispatch(searching(true));
    fetch(URLS.UNSOLDHIDE, {
        body: JSON.stringify({ ids: itemIds })
    })
        .then(() => {
            dispatch(error(false));
            dispatch(unsoldItemsHidden(itemIds, listViewType));
            dispatch(searching(false));
            return;
        })
        .catch(error => handleError(dispatch, error));
};

const buyingLeaveFeedback = (item, listViewType) => () => {
    const payload = {
        action: 'LeaveFeedbackAsBuyer',
        ids: [item.transaction.transactionId],
        itemId: item.itemId,
        listViewType: listViewType
    };
    triot.bus.trigger('ui:list-item-action:on-leave-single-feedback', payload);
    return payload;
};

const setNewShippingPrice = (purchaseOrderId, shippingProvider, newPrice) => ({
    type: actionTypes.SET_NEW_SHIPPING_PRICE,
    purchaseOrderId,
    shippingProvider,
    newPrice
});

export default {
    resetMyTraderaItems,
    transactionsUnmarkAsShippedBySeller,
    transactionsMarkAsShippedBySeller,
    handleError,
    actionTypes,
    cancelItem,
    buyerActiveItemsHide,
    buyerActiveItemsShow,
    buyerActiveItemsHideItems, // More menu
    buyerActiveItemsShowItems, // More menu
    updateItems,
    resetSelectedCheckboxes,
    openMore,
    updateURL,
    runSearch,
    selectItem,
    removeItems,
    selectAllItems,
    contactHighestBidder,
    markPurchaseOrdersAsPaidBySeller,
    unmarkPurchaseOrdersAsPaidBySeller,
    rebuildList,
    sellerUnhideTransaction,
    sellerHideTransaction,
    sellerUnhideUnsold,
    sellerHideUnsold,
    sellingMarkAsShippingBooked,
    sellingMarkAsShipped,
    sellingUnmarkAsShipped,
    sellingUnmarkAsShippingBooked,
    sellingDeleteShipment,
    sellingLeaveFeedback,
    feedbackLeftBySeller,
    feedbackLeftByBuyer,
    markPurchaseOrdersAsPaidByBuyer,
    unmarkPurchaseOrdersAsPaidByBuyer,
    buyerUnhideTransaction,
    buyerHideTransaction,
    buyingLeaveFeedback,
    setRestarted,
    setNewShippingPrice,
    purchaseOrdersMarkAsPaidBySeller,
    purchaseOrdersUnmarkAsPaidBySeller,
    purchaseOrdersMarkAsPaidByBuyer,
    purchaseOrdersUnmarkAsPaidByBuyer,
    changeParamsForBackend
};
